import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getConfig } from "../../db/queries";
import {
  agendamentosNoIntervalo,
  getClinicaAtiva,
  listarMembros,
} from "../queries";
import {
  DIAS_SEMANA_CURTO,
  corMembro,
  dataLegivel,
  ehEncerrado,
  hojeChave,
  horaFim,
  matrizMes,
  porHora,
  rotuloMes,
  somaMes,
} from "../domain";
import type { Agendamento } from "../types";
import { TIPO_LABEL } from "../types";
import { StatusAgendaBadge } from "../ui/StatusAgendaBadge";

/**
 * Calendário da clínica (Agenda, Fase 1 — provedor nativo). Grade do mês com
 * pontos coloridos por profissional + lista cronológica do dia selecionado.
 */
export function AgendaCalendario() {
  const nav = useNavigate();
  const cfg = useLiveQuery(getConfig);
  const clinica = useLiveQuery(getClinicaAtiva);
  const clinicaId = clinica?.id;

  const hoje = hojeChave();
  const inicial = useMemo(() => {
    const d = new Date();
    return { ano: d.getFullYear(), mes: d.getMonth() };
  }, []);
  const [ano, setAno] = useState(inicial.ano);
  const [mes, setMes] = useState(inicial.mes);
  const [selecionado, setSelecionado] = useState(hoje);

  const semanas = useMemo(() => matrizMes(ano, mes), [ano, mes]);
  const deChave = semanas[0][0].chave;
  const ateChave = semanas[5][6].chave;

  const membros = useLiveQuery(
    () => (clinicaId ? listarMembros(clinicaId) : Promise.resolve([])),
    [clinicaId],
  );
  const corPorProf = useMemo(() => {
    const m = new Map<string, string>();
    (membros ?? []).forEach((p) => m.set(p.id, corMembro(p.cor, p.especialidade)));
    return m;
  }, [membros]);
  const nomePorProf = useMemo(() => {
    const m = new Map<string, string>();
    (membros ?? []).forEach((p) => m.set(p.id, p.nome));
    return m;
  }, [membros]);

  const doMes = useLiveQuery(
    () =>
      clinicaId
        ? agendamentosNoIntervalo(clinicaId, deChave, ateChave)
        : Promise.resolve([]),
    [clinicaId, deChave, ateChave],
  );

  // agrupa por dia (só compromissos ativos contam como "ponto" no dia)
  const porDia = useMemo(() => {
    const m = new Map<string, Agendamento[]>();
    (doMes ?? []).forEach((a) => {
      const arr = m.get(a.data) ?? [];
      arr.push(a);
      m.set(a.data, arr);
    });
    return m;
  }, [doMes]);

  const doDia = useMemo(
    () => (porDia.get(selecionado) ?? []).slice().sort(porHora),
    [porDia, selecionado],
  );

  function navegarMes(delta: number) {
    const r = somaMes(ano, mes, delta);
    setAno(r.ano);
    setMes(r.mes);
  }
  function irHoje() {
    const d = new Date();
    setAno(d.getFullYear());
    setMes(d.getMonth());
    setSelecionado(hojeChave());
  }

  // guarda local-first: rota acessada sem agenda ativa (deep link)
  if (cfg && (!cfg.agenda_ativa || !clinicaId)) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav("/")}>← módulos</button>
        <h1 className="tela-titulo">agenda</h1>
        <div className="ag-aviso neutro">
          a agenda da clínica ainda não está ativada neste aparelho.
        </div>
        <Link to="/agenda/config" className="btn btn-primario btn-bloco">
          ativar agenda da clínica
        </Link>
      </main>
    );
  }

  return (
    <main className="tela">
      <div className="row-entre">
        <h1 className="tela-titulo" style={{ marginBottom: 0 }}>agenda</h1>
        <Link to="/agenda/novo" className="btn btn-primario">➕ nova consulta</Link>
      </div>
      <p className="tela-sub">
        {clinica?.nome ?? "clínica"} · calendário
      </p>

      <div className="ag-toolbar">
        <button className="ag-nav-btn" aria-label="mês anterior" onClick={() => navegarMes(-1)}>‹</button>
        <div className="ag-mes">{rotuloMes(ano, mes)}</div>
        <button className="ag-nav-btn" aria-label="próximo mês" onClick={() => navegarMes(1)}>›</button>
      </div>

      <div className="ag-cal">
        <div className="ag-semana-cab" aria-hidden>
          {DIAS_SEMANA_CURTO.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="ag-grade">
          {semanas.flat().map((c) => {
            const evs = (porDia.get(c.chave) ?? []).filter((a) => !ehEncerrado(a.status));
            const classes = [
              "ag-cel",
              c.noMes ? "" : "fora",
              c.hoje ? "hoje" : "",
              c.chave === selecionado ? "sel" : "",
            ].filter(Boolean).join(" ");
            return (
              <button
                key={c.chave}
                className={classes}
                onClick={() => setSelecionado(c.chave)}
                aria-label={`${c.dia}${evs.length ? `, ${evs.length} consulta(s)` : ""}`}
              >
                <span className="ag-cel-num">{c.dia}</span>
                <span className="ag-pontos">
                  {evs.slice(0, 4).map((a) => (
                    <span
                      key={a.id}
                      className="ag-ponto"
                      style={{ background: corPorProf.get(a.profissional_id) ?? "var(--text-muted)" }}
                    />
                  ))}
                  {evs.length > 4 && <span className="ag-cel-vazio-mais">+{evs.length - 4}</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="ag-dia-cab">
        <span className="ag-dia-titulo">{dataLegivel(selecionado)}</span>
        <span className="ag-dia-cont">
          {selecionado !== hoje && (
            <button className="ag-hoje-link" onClick={irHoje}>ir para hoje</button>
          )}
          {" "}
          {doDia.length} consulta{doDia.length === 1 ? "" : "s"}
        </span>
      </div>

      {doDia.length === 0 ? (
        <div className="vazio">
          <span className="emoji" aria-hidden>🗓️</span>
          nada agendado neste dia.
          <div style={{ marginTop: "var(--s3)" }}>
            <Link to={`/agenda/novo?data=${selecionado}`} className="btn">➕ agendar aqui</Link>
          </div>
        </div>
      ) : (
        <div className="ag-lista">
          {doDia.map((a) => (
            <button
              key={a.id}
              className={`ag-item ${ehEncerrado(a.status) ? "encerrado" : ""}`}
              onClick={() => nav(`/agenda/${a.id}`)}
            >
              <span
                className="ag-item-faixa"
                style={{ background: corPorProf.get(a.profissional_id) ?? "var(--border-forte)" }}
              />
              <span className="ag-item-hora">
                {a.hora_inicio}
                <span className="fim">{horaFim(a.hora_inicio, a.duracao_min)}</span>
              </span>
              <span className="ag-item-corpo">
                <span className="row-entre" style={{ gap: "var(--s2)" }}>
                  <span className="ag-item-nome">{a.paciente_nome}</span>
                  <StatusAgendaBadge status={a.status} />
                </span>
                <span className="ag-item-meta">
                  <span className="ag-prof-dot" style={{ background: corPorProf.get(a.profissional_id) ?? "var(--text-muted)" }} />
                  {nomePorProf.get(a.profissional_id) ?? "profissional"}
                  {" · "}{TIPO_LABEL[a.tipo]}
                  {a.modalidade === "online" ? " · online" : ""}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </main>
  );
}
